import { useKeenSlider } from "keen-slider/react";
import Image from "next/legacy/image";
import React from "react";

import "keen-slider/keen-slider.min.css";

import { blurDataURL } from "../lib/constants";
import { imageBuilder } from "../lib/sanity";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/solid";

export default function ProjectImageSlider({ images }) {
  const [loaded, setLoaded] = React.useState([]);
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const [sliderRef, propsRef] = useKeenSlider({
    animationEnded(s) {
      setCurrentSlide(s.track.details.rel);
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    loop: true,
    initial: 0,
  });

  React.useEffect(() => {
    const new_loaded = [...loaded];
    new_loaded[currentSlide] = true;
    new_loaded[currentSlide + 1] = true;
    setLoaded(new_loaded);
  }, [currentSlide]);

  return (
    <div>
      <div ref={sliderRef} className="keen-slider rounded-lg">
        {images.map((image, idx) => (
          <div key={idx} className="keen-slider__slide">
            <div className="aspect-w-4 aspect-h-3 bg-gray-100 overflow-hidden">
              {loaded[idx] ? (
                <Image
                  draggable={false}
                  className={"object-center object-cover"}
                  alt=""
                  layout="fill"
                  priority={currentSlide === idx}
                  placeholder="blur"
                  blurDataURL={blurDataURL}
                  src={imageBuilder(image)
                    .width(1280)
                    .height(960)
                    .crop("entropy")
                    .url()}
                />
              ) : null}
            </div>
          </div>
        ))}

        {loaded && propsRef.current && (
          <>
            <Arrow
              left
              onClick={(e) => e.stopPropagation() || propsRef.current?.prev()}
              disabled={currentSlide === 0}
            />

            <Arrow
              onClick={(e) => e.stopPropagation() || propsRef.current?.next()}
              disabled={
                currentSlide ===
                propsRef.current.track.details.slides.length - 1
              }
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-6 gap-4 mt-4">
        {images.map((img, index) => {
          const active = index === currentSlide;
          return (
            <div
              key={`thumbnail_${img.asset["_ref"]}_${index}`}
              className={`shadow rounded cursor-pointer transition group aspect-w-4 aspect-h-3 bg-gray-100 overflow-hidden ${
                active ? "ring-2 ring-accent" : ""
              }`}
              onClick={() => {
                if (!loaded || !propsRef.current) return;
                setCurrentSlide(index);
                propsRef.current?.moveToIdx(index, false, {
                  duration: 0,
                });
              }}
            >
              <Image
                draggable={false}
                className="object-center object-cover sm:group-hover:opacity-75 transition-opacity"
                alt=""
                layout="fill"
                placeholder="blur"
                blurDataURL={blurDataURL}
                src={imageBuilder(img)
                  .width(73)
                  .height(55)
                  .dpr(2)
                  .crop("entropy")
                  .url()}
              />

              <div
                className={`absolute inset-0 bg-gradient-to-l from-accent-300 to-accent-400 mix-blend-multiply transition-opacity ${
                  active ? "opacity-1" : "opacity-0"
                }`}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Arrow(props) {
  const iconClassName = `hidden ${
    props.disabled ? "text-opacity-50" : ""
  } sm:inline-flex w-12 h-12 absolute top-1/2 -translate-y-1/2 text-white cursor-pointer`;
  return props.left ? (
    <ChevronLeftIcon
      className={`${iconClassName} left-1`}
      onClick={props.onClick}
    />
  ) : (
    <ChevronRightIcon
      className={`${iconClassName} right-1`}
      onClick={props.onClick}
    />
  );
}
